import { useCallback, useEffect, useRef, useState } from "react";
import { vendorInvoiceRepository } from "../repositories/vendorInvoice.repository";

const EMPTY_LIST = {
  count: 0,
  next: null,
  previous: null,
  results: [],
  invoices: [],
};

export const useVendorInvoicesOffline = (
  authToken,
  orgToken,
  page = 1,
  pageSize = 10,
  sortKey = "",
  sortOrder = ""
) => {
  const [data, setData] = useState(EMPTY_LIST);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastRequestKeyRef = useRef("");
  const pageToNextRef = useRef(new Map());

  const loadPage = useCallback(
    async (targetPage = 1, force = false) => {
      if (!orgToken) {
        setLoading(false);
        return;
      }

      const normalizedPage = Number(targetPage || 1);
      const normalizedPageSize = Number(pageSize || 10);
      const normalizedSortKey = String(sortKey || "");
      const normalizedSortOrder = String(sortOrder || "");

      const requestKey = JSON.stringify({
        orgToken: String(orgToken || ""),
        page: normalizedPage,
        pageSize: normalizedPageSize,
        sortKey: normalizedSortKey,
        sortOrder: normalizedSortOrder,
        online: typeof navigator !== "undefined" ? navigator.onLine : false,
      });

      if (!force && lastRequestKeyRef.current === requestKey) return;
      lastRequestKeyRef.current = requestKey;

      setLoading(true);
      setError(null);

      const cacheFallback = async () => {
        const cached = await vendorInvoiceRepository.getVendorInvoicesPage(
          orgToken,
          normalizedPage,
          normalizedPageSize,
          normalizedSortKey,
          normalizedSortOrder
        );
        if (cached) {
          setData({
            count: Number(cached?.count || 0),
            next: cached?.next || null,
            previous: cached?.previous || null,
            results: cached?.results || [],
            invoices: cached?.invoices || [],
          });
          return true;
        }
        setData(EMPTY_LIST);
        return false;
      };

      try {
        const isOnline =
          typeof navigator !== "undefined" ? navigator.onLine : false;
        const canCallAPI = isOnline && !!orgToken;

        if (canCallAPI) {
          const response = await vendorInvoiceRepository.fetchVendorInvoicesFromAPI(
            authToken,
            orgToken,
            {
              page: normalizedPage,
              pageSize: normalizedPageSize,
              sortKey: normalizedSortKey,
              sortOrder: normalizedSortOrder,
            }
          );

          setData(response || EMPTY_LIST);

          if (response?.next) {
            const nextKey = `${normalizedPage}|${normalizedSortKey}|${normalizedSortOrder}`;
            pageToNextRef.current.set(nextKey, response.next);
          }
        } else {
          await cacheFallback();
        }
      } catch (err) {
        setError(err);
        await cacheFallback();
      } finally {
        setLoading(false);
      }
    },
    [authToken, orgToken, pageSize, sortKey, sortOrder]
  );

  const loadNext = useCallback(
    async (force = false) => {
      const nextKey = `${Number(page)}|${String(sortKey || "")}|${String(
        sortOrder || ""
      )}`;
      const currentNext = data?.next || pageToNextRef.current.get(nextKey);
      if (!currentNext) return;
      let nextPage = Number(page) + 1;
      try {
        const parsed = new URL(currentNext, window.location.origin);
        const pageFromUrl = Number(parsed.searchParams.get("page") || 0);
        if (pageFromUrl > 0) nextPage = pageFromUrl;
      } catch (e) {
        // Ignore parsing errors and use sequential page fallback.
      }
      await loadPage(nextPage, force);
    },
    [data?.next, loadPage, page, sortKey, sortOrder]
  );

  useEffect(() => {
    loadPage(page);
  }, [loadPage, page]);

  return {
    data,
    invoices: data?.invoices || [],
    totalCount: Number(data?.count || 0),
    next: data?.next || null,
    previous: data?.previous || null,
    loading,
    error,
    loadPage,
    loadNext,
    refetch: () => loadPage(page, true),
  };
};

const EMPTY_DETAILS = {
  organization: "",
  org_id: "",
  invoices: [],
  count: 0,
  next: null,
  previous: null,
};

const getCountFromDetails = (details) => {
  if (!details || typeof details !== "object") return 0;

  const totalFromResponse =
    details?.total_count ??
    details?.totalCount ??
    details?.pagination?.total_items ??
    details?.pagination?.totalItems;

  if (totalFromResponse != null) {
    return Number(totalFromResponse || 0);
  }

  if (details?.count != null) {
    return Number(details.count || 0);
  }

  return Number(details?.invoices?.length || 0);
};

export const useVendorInvoiceDetailsOffline = (
  authToken,
  token,
  page = 1
) => {
  const [data, setData] = useState(EMPTY_DETAILS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastRequestKeyRef = useRef("");

  const loadDetails = useCallback(
    async (force = false) => {
      if (!token) {
        setLoading(false);
        return;
      }

      const normalizedPage = Number(page || 1);

      const requestKey = JSON.stringify({
        token: String(token || ""),
        page: normalizedPage,
        online: typeof navigator !== "undefined" ? navigator.onLine : false,
      });

      if (!force && lastRequestKeyRef.current === requestKey) return;
      lastRequestKeyRef.current = requestKey;

      setLoading(true);
      setError(null);

      const cacheFallback = async () => {
        const cached = await vendorInvoiceRepository.getVendorInvoiceDetails(
          token,
          normalizedPage
        );
        if (cached?.data) {
          setData({ ...EMPTY_DETAILS, ...cached.data });
          return true;
        }
        setData(EMPTY_DETAILS);
        return false;
      };

      try {
        const isOnline =
          typeof navigator !== "undefined" ? navigator.onLine : false;
        const canCallAPI = isOnline && !!token;

        if (canCallAPI) {
          const response =
            await vendorInvoiceRepository.fetchVendorInvoiceDetailsFromAPI(
              authToken,
              token,
              { page: normalizedPage }
            );
          setData({ ...EMPTY_DETAILS, ...(response || {}) });
        } else {
          await cacheFallback();
        }
      } catch (err) {
        setError(err);
        await cacheFallback();
      } finally {
        setLoading(false);
      }
    },
    [authToken, token, page]
  );

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  return {
    data,
    invoices: data?.invoices || [],
    totalCount: getCountFromDetails(data),
    next: data?.next || null,
    previous: data?.previous || null,
    loading,
    error,
    refetch: () => loadDetails(true),
  };
};
